import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  FileText, 
  Plus, 
  Cloud, 
  Thermometer, 
  Users, 
  Calendar,
  Wrench,
  Package,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

export const DailyReportsPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    date: today,
    weather: "",
    temperature: "",
    workers_on_site: 0,
    work_performed: "",
    materials_used: "",
    equipment_used: "",
    issues: "",
    notes: ""
  });

  const weatherOptions = [
    { value: "sunny", label: language === "sv" ? "Soligt" : "Sunny", icon: "☀️" },
    { value: "cloudy", label: language === "sv" ? "Molnigt" : "Cloudy", icon: "☁️" },
    { value: "rainy", label: language === "sv" ? "Regnigt" : "Rainy", icon: "🌧️" },
    { value: "snowy", label: language === "sv" ? "Snöigt" : "Snowy", icon: "❄️" },
    { value: "windy", label: language === "sv" ? "Blåsigt" : "Windy", icon: "💨" }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchReports(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
        if (data.length > 0) {
          setSelectedProject(data[0].project_id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/daily-reports`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setReports(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;

    try {
      const res = await fetch(`${API}/projects/${selectedProject}/daily-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(language === "sv" ? "Dagrapport sparad!" : "Daily report saved!");
        setDialogOpen(false);
        setFormData({
          date: today,
          weather: "",
          temperature: "",
          workers_on_site: 0,
          work_performed: "",
          materials_used: "",
          equipment_used: "",
          issues: "",
          notes: ""
        });
        fetchReports(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    }
  };

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="daily-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Dagrapporter" : "Daily Reports"}
            </h1>
            <p className="text-slate-600 mt-1">
              {language === "sv" 
                ? "Dokumentera dagliga arbeten och händelser" 
                : "Document daily work and events"}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-touch" disabled={!selectedProject} data-testid="create-report-btn">
                <Plus className="w-5 h-5 mr-2" />
                {language === "sv" ? "Ny dagrapport" : "New Report"}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                  {language === "sv" ? "Skapa dagrapport" : "Create Daily Report"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Datum" : "Date"}</Label>
                    <Input 
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      data-testid="report-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Personal på plats" : "Workers on site"}</Label>
                    <Input 
                      type="number"
                      value={formData.workers_on_site}
                      onChange={(e) => setFormData({...formData, workers_on_site: parseInt(e.target.value) || 0})}
                      data-testid="report-workers-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Väder" : "Weather"}</Label>
                    <Select value={formData.weather} onValueChange={(v) => setFormData({...formData, weather: v})}>
                      <SelectTrigger data-testid="report-weather-select">
                        <SelectValue placeholder={language === "sv" ? "Välj väder" : "Select weather"} />
                      </SelectTrigger>
                      <SelectContent>
                        {weatherOptions.map(w => (
                          <SelectItem key={w.value} value={w.value}>
                            {w.icon} {w.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Temperatur" : "Temperature"}</Label>
                    <Input 
                      placeholder={language === "sv" ? "t.ex. +5°C" : "e.g. +5°C"}
                      value={formData.temperature}
                      onChange={(e) => setFormData({...formData, temperature: e.target.value})}
                      data-testid="report-temp-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Utfört arbete" : "Work performed"} *</Label>
                  <Textarea 
                    value={formData.work_performed}
                    onChange={(e) => setFormData({...formData, work_performed: e.target.value})}
                    placeholder={language === "sv" ? "Beskriv dagens arbete..." : "Describe today's work..."}
                    rows={3}
                    required
                    data-testid="report-work-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Material använt" : "Materials used"}</Label>
                    <Textarea 
                      value={formData.materials_used}
                      onChange={(e) => setFormData({...formData, materials_used: e.target.value})}
                      placeholder={language === "sv" ? "Lista material..." : "List materials..."}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{language === "sv" ? "Utrustning" : "Equipment"}</Label>
                    <Textarea 
                      value={formData.equipment_used}
                      onChange={(e) => setFormData({...formData, equipment_used: e.target.value})}
                      placeholder={language === "sv" ? "Lista maskiner/verktyg..." : "List machines/tools..."}
                      rows={2}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="form-label text-amber-600">{language === "sv" ? "Problem/Avvikelser" : "Issues/Deviations"}</Label>
                  <Textarea 
                    value={formData.issues}
                    onChange={(e) => setFormData({...formData, issues: e.target.value})}
                    placeholder={language === "sv" ? "Beskriv eventuella problem..." : "Describe any issues..."}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="form-label">{language === "sv" ? "Övriga noteringar" : "Additional notes"}</Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder={language === "sv" ? "Övriga anteckningar..." : "Other notes..."}
                    rows={2}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    {language === "sv" ? "Avbryt" : "Cancel"}
                  </Button>
                  <Button type="submit" className="flex-1" data-testid="report-submit-btn">
                    {language === "sv" ? "Spara" : "Save"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Project Filter */}
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full sm:w-[300px] h-12" data-testid="report-project-select">
            <SelectValue placeholder={language === "sv" ? "Välj projekt" : "Select project"} />
          </SelectTrigger>
          <SelectContent>
            {projects.map(p => (
              <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Reports List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !selectedProject ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {language === "sv" ? "Välj ett projekt" : "Select a project"}
              </p>
            </CardContent>
          </Card>
        ) : reports.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">
                {language === "sv" ? "Inga dagrapporter ännu" : "No daily reports yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => {
              const weather = weatherOptions.find(w => w.value === report.weather);
              return (
                <Card key={report.report_id} data-testid={`report-card-${report.report_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>
                          <Calendar className="w-5 h-5 inline mr-2 text-slate-400" />
                          {report.date}
                        </CardTitle>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        {weather && <span>{weather.icon}</span>}
                        {report.temperature && (
                          <span className="flex items-center gap-1">
                            <Thermometer className="w-4 h-4" />
                            {report.temperature}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {report.workers_on_site}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-1">
                        {language === "sv" ? "Utfört arbete" : "Work performed"}
                      </h4>
                      <p className="text-slate-600">{report.work_performed}</p>
                    </div>
                    
                    {(report.materials_used || report.equipment_used) && (
                      <div className="grid grid-cols-2 gap-4">
                        {report.materials_used && (
                          <div>
                            <h4 className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1">
                              <Package className="w-3 h-3" />
                              {language === "sv" ? "Material" : "Materials"}
                            </h4>
                            <p className="text-sm text-slate-600">{report.materials_used}</p>
                          </div>
                        )}
                        {report.equipment_used && (
                          <div>
                            <h4 className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1">
                              <Wrench className="w-3 h-3" />
                              {language === "sv" ? "Utrustning" : "Equipment"}
                            </h4>
                            <p className="text-sm text-slate-600">{report.equipment_used}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {report.issues && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="text-sm font-semibold text-amber-700 flex items-center gap-1 mb-1">
                          <AlertTriangle className="w-4 h-4" />
                          {language === "sv" ? "Problem/Avvikelser" : "Issues"}
                        </h4>
                        <p className="text-sm text-amber-800">{report.issues}</p>
                      </div>
                    )}
                    
                    {report.notes && (
                      <div>
                        <h4 className="text-xs font-semibold text-slate-500 mb-1">
                          {language === "sv" ? "Noteringar" : "Notes"}
                        </h4>
                        <p className="text-sm text-slate-600">{report.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};
